import React from 'react';

import { COLORS } from '@/nova/theme/tokens';

/**
 * Inline SVG icon set for the nova area. No external asset files —
 * every glyph is drawn here so a single import covers the whole area and the
 * stroke colour follows `currentColor` by default.
 *
 * Icons with a source mapping render the exact source vector (verbatim viewBox +
 * inner markup), made tintable via the `color` prop. Icons without a source
 * mapping keep their original hand-drawn markup.
 *
 * To add an icon: add its name to `IconName` and a `case` below.
 */
export type IconName =
  | 'home'
  | 'user'
  | 'user-filled'
  | 'sparkle'
  | 'arrow-right-circle'
  | 'globe'
  | 'mobile'
  | 'crypto'
  | 'video'
  | 'wallet'
  | 'calendar'
  | 'edit'
  | 'copy'
  | 'affiliates'
  | 'influencers'
  | 'search'
  | 'filter'
  | 'clock'
  | 'vibe'
  | 'document'
  | 'palette'
  | 'lock'
  | 'trending-up'
  | 'trending-down'
  | 'arrow-left'
  | 'heart'
  | 'check'
  | 'plus'
  | 'plus-circle'
  | 'download'
  | 'pause'
  | 'stop'
  | 'chevron-expand'
  | 'chevron-up'
  | 'grab'
  | 'minus'
  | 'close'
  | 'close-circle'
  | 'warning'
  | 'info'
  | 'settings'
  | 'external-link'
  | 'more'
  | 'eye'
  | 'trash'
  | 'drag-handle'
  | 'megaphone'
  | 'grid'
  | 'mail'
  | 'send'
  | 'refresh'
  | 'eye-off'
  | 'shield'
  | 'chevron-down'
  | 'image'
  | 'preview'
  | 'reorder';

export type IconProps = {
  name: IconName;
  size?: number;
  color?: string;
  className?: string;
};

export const Icon = ({ name, size = 20, color = 'currentColor', className }: IconProps): React.ReactElement | null => {
  const stroke = (children: React.ReactNode) => (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );

  switch (name) {
    case 'home':
      // Source: figmaIcons → Home (fill)
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M4 19V10C4 9.68333 4.071 9.38333 4.213 9.1C4.355 8.81667 4.55067 8.58333 4.8 8.4L10.8 3.9C11.15 3.63333 11.55 3.5 12 3.5C12.45 3.5 12.85 3.63333 13.2 3.9L19.2 8.4C19.45 8.58333 19.646 8.81667 19.788 9.1C19.93 9.38333 20.0007 9.68333 20 10V19C20 19.55 19.804 20.021 19.412 20.413C19.02 20.805 18.5493 21.0007 18 21H15C14.7167 21 14.4793 20.904 14.288 20.712C14.0967 20.52 14.0007 20.2827 14 20V15C14 14.7167 13.904 14.4793 13.712 14.288C13.52 14.0967 13.2827 14.0007 13 14H11C10.7167 14 10.4793 14.096 10.288 14.288C10.0967 14.48 10.0007 14.7173 10 15V20C10 20.2833 9.904 20.521 9.712 20.713C9.52 20.905 9.28267 21.0007 9 21H6C5.45 21 4.97933 20.8043 4.588 20.413C4.19667 20.0217 4.00067 19.5507 4 19Z" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M12 2c.5 4.9 2.6 7 7.5 7.5-4.9.5-7 2.6-7.5 7.5-.5-4.9-2.6-7-7.5-7.5 4.9-.5 7-2.6 7.5-7.5z" />
        </svg>
      );
    case 'arrow-right-circle':
      // Source: figmaIcons → ArrowCircleRight (stroke)
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
          <path d="M10.74 15.53L14.26 12L10.74 8.47" strokeMiterlimit={undefined} />
        </svg>
      );
    case 'globe':
      // Source: figmaIcons → Globe (fill)
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 21.5 21.5" fill={color}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.95646 1.93191C7.70433 2.01179 7.45533 2.10257 7.21018 2.20411C6.08792 2.66897 5.0682 3.35032 4.20926 4.20926C3.35032 5.0682 2.66897 6.08792 2.20411 7.21018C1.8352 8.10082 1.60837 9.0422 1.53045 10H6.01094C6.04294 8.9041 6.14498 7.8241 6.31457 6.80057C6.5225 5.54558 6.82972 4.38914 7.22522 3.40039C7.44114 2.86057 7.6851 2.36641 7.95646 1.93191ZM10.75 0C9.33829 -1.78814e-07 7.9404 0.278057 6.63615 0.818295C5.3319 1.35853 4.14683 2.15037 3.1486 3.1486C2.15037 4.14683 1.35853 5.3319 0.818295 6.63615C0.278057 7.9404 1.19209e-07 9.33829 0 10.75C-1.19209e-07 12.1617 0.278057 13.5596 0.818295 14.8638C1.35853 16.1681 2.15037 17.3532 3.1486 18.3514C4.14683 19.3496 5.3319 20.1415 6.63615 20.6817C7.9404 21.2219 9.33829 21.5 10.75 21.5C12.1617 21.5 13.5596 21.2219 14.8638 20.6817C16.1681 20.1415 17.3532 19.3496 18.3514 18.3514C19.3496 17.3532 20.1415 16.1681 20.6817 14.8638C21.2219 13.5596 21.5 12.1617 21.5 10.75C21.5 9.33829 21.2219 7.9404 20.6817 6.63615C20.1415 5.3319 19.3496 4.14683 18.3514 3.1486C17.3532 2.15037 16.1681 1.35853 14.8638 0.818296C13.5596 0.278057 12.1617 1.78814e-07 10.75 0ZM10.75 1.5C10.4887 1.5 10.1512 1.62579 9.75877 2.0322C9.36341 2.44161 8.96762 3.08326 8.61793 3.95747C8.27056 4.82589 7.9885 5.87424 7.79439 7.04576C7.63866 7.98569 7.54316 8.98313 7.51162 10L13.9884 10C13.9568 8.98313 13.8613 7.98569 13.7056 7.04576C13.5115 5.87424 13.2294 4.8259 12.8821 3.95748C12.5324 3.08326 12.1366 2.44161 11.7412 2.0322C11.3488 1.62579 11.0113 1.5 10.75 1.5ZM15.4891 10C15.4571 8.9041 15.355 7.82411 15.1854 6.80057C14.9775 5.54558 14.6703 4.38914 14.2748 3.40039C14.0589 2.86057 13.8149 2.36641 13.5435 1.93191C13.7957 2.01179 14.0447 2.10257 14.2898 2.20411C15.4121 2.66897 16.4318 3.35032 17.2907 4.20926C18.1497 5.06821 18.831 6.08792 19.2959 7.21018C19.6648 8.10082 19.8916 9.0422 19.9695 10H15.4891ZM13.9884 11.5L7.51162 11.5C7.54316 12.5169 7.63866 13.5143 7.79439 14.4542C7.9885 15.6258 8.27056 16.6741 8.61793 17.5425C8.96762 18.4167 9.36341 19.0584 9.75877 19.4678C10.1512 19.8742 10.4887 20 10.75 20C11.0113 20 11.3488 19.8742 11.7412 19.4678C12.1366 19.0584 12.5324 18.4167 12.8821 17.5425C13.2294 16.6741 13.5115 15.6258 13.7056 14.4542C13.8613 13.5143 13.9568 12.5169 13.9884 11.5ZM13.5435 19.5681C13.8149 19.1336 14.0589 18.6394 14.2748 18.0996C14.6703 17.1109 14.9775 15.9544 15.1854 14.6994C15.355 13.6759 15.4571 12.5959 15.4891 11.5H19.9695C19.8916 12.4578 19.6648 13.3992 19.2959 14.2898C18.831 15.4121 18.1497 16.4318 17.2907 17.2907C16.4318 18.1497 15.4121 18.831 14.2898 19.2959C14.0447 19.3974 13.7957 19.4882 13.5435 19.5681ZM7.95646 19.5681C7.6851 19.1336 7.44114 18.6394 7.22521 18.0996C6.82971 17.1109 6.5225 15.9544 6.31457 14.6994C6.14498 13.6759 6.04294 12.5959 6.01094 11.5H1.53045C1.60837 12.4578 1.8352 13.3992 2.20411 14.2898C2.66897 15.4121 3.35032 16.4318 4.20926 17.2907C5.0682 18.1497 6.08792 18.831 7.21018 19.2959C7.45533 19.3974 7.70433 19.4882 7.95646 19.5681Z"
          />
        </svg>
      );
    case 'mobile':
      // Source: figmaIcons → Smartphone (fill)
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 17.5 21.5" fill={color}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.69358 3.21614e-07H8.80642C10.6442 -1.55332e-05 12.0998 -2.81632e-05 13.239 0.153135C14.4114 0.310763 15.3604 0.64288 16.1088 1.39124C16.8571 2.13961 17.1892 3.08856 17.3469 4.26098C17.5 5.40019 17.5 6.85583 17.5 8.69359V12.8064C17.5 14.6442 17.5 16.0998 17.3469 17.239C17.1892 18.4114 16.8571 19.3604 16.1088 20.1088C15.3604 20.8571 14.4114 21.1892 13.239 21.3469C12.0998 21.5 10.6442 21.5 8.80641 21.5H8.69359C6.85583 21.5 5.40019 21.5 4.26098 21.3469C3.08856 21.1892 2.13961 20.8571 1.39124 20.1088C0.64288 19.3604 0.310763 18.4114 0.153135 17.239C-2.81632e-05 16.0998 -1.55332e-05 14.6442 3.21614e-07 12.8064V8.69358C-1.55332e-05 6.85582 -2.81632e-05 5.40019 0.153135 4.26098C0.310763 3.08856 0.64288 2.13961 1.39124 1.39124C2.13961 0.64288 3.08856 0.310763 4.26098 0.153135C5.40019 -2.81632e-05 6.85582 -1.55332e-05 8.69358 3.21614e-07ZM4.46085 1.63976C3.45476 1.77502 2.87511 2.02869 2.4519 2.4519C2.02869 2.87511 1.77502 3.45476 1.63976 4.46085C1.50159 5.48851 1.5 6.84318 1.5 8.75V12.75C1.5 14.6568 1.50159 16.0115 1.63976 17.0392C1.77502 18.0452 2.02869 18.6249 2.4519 19.0481C2.87511 19.4713 3.45476 19.725 4.46085 19.8602C5.48851 19.9984 6.84318 20 8.75 20C10.6568 20 12.0115 19.9984 13.0392 19.8602C14.0452 19.725 14.6249 19.4713 15.0481 19.0481C15.4713 18.6249 15.725 18.0452 15.8602 17.0392C15.9984 16.0115 16 14.6568 16 12.75V8.75C16 6.84318 15.9984 5.48851 15.8602 4.46085C15.725 3.45476 15.4713 2.87511 15.0481 2.4519C14.6249 2.02869 14.0452 1.77502 13.0392 1.63976C12.0115 1.50159 10.6568 1.5 8.75 1.5C6.84318 1.5 5.48851 1.50159 4.46085 1.63976ZM5 17.75C5 17.3358 5.33579 17 5.75 17H11.75C12.1642 17 12.5 17.3358 12.5 17.75C12.5 18.1642 12.1642 18.5 11.75 18.5H5.75C5.33579 18.5 5 18.1642 5 17.75Z"
          />
        </svg>
      );
    case 'crypto':
      // Source: figmaIcons → Bitcoin (fill)
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 19.5 19.5" fill={color}>
          <path d="M9.75 0C4.37 0 0 4.37 0 9.75C0 15.13 4.37 19.5 9.75 19.5C15.13 19.5 19.5 15.13 19.5 9.75C19.5 4.37 15.13 0 9.75 0ZM9.75 18C5.2 18 1.5 14.3 1.5 9.75C1.5 5.2 5.2 1.5 9.75 1.5C14.3 1.5 18 5.2 18 9.75C18 14.3 14.3 18 9.75 18Z" />
          <path d="M11.75 5H5.75C5.34 5 5 5.34 5 5.75C5 6.16 5.34 6.5 5.75 6.5H6V13H5.75C5.34 13 5 13.34 5 13.75C5 14.16 5.34 14.5 5.75 14.5H11.75C13.27 14.5 14.5 13.27 14.5 11.75C14.5 10.96 14.17 10.25 13.64 9.75C14.17 9.25 14.5 8.54 14.5 7.75C14.5 6.23 13.27 5 11.75 5ZM11.75 13H7.5V10.5H11.75C12.44 10.5 13 11.06 13 11.75C13 12.44 12.44 13 11.75 13ZM11.75 9H7.5V6.5H11.75C12.44 6.5 13 7.06 13 7.75C13 8.44 12.44 9 11.75 9Z" />
        </svg>
      );
    case 'video':
      // Source: figmaIcons → Clapperboard (fill)
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 21.5 21.5" fill={color}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.6926 2.1154e-07H10.8074C13.1158 -1.26631e-05 14.9248 -2.28584e-05 16.3363 0.189747C17.781 0.383986 18.9211 0.789327 19.8159 1.68414C20.7107 2.57895 21.116 3.71897 21.3103 5.16371C21.5 6.57519 21.5 8.38423 21.5 10.6926V10.8074C21.5 13.1158 21.5 14.9248 21.3103 16.3363C21.116 17.781 20.7107 18.9211 19.8159 19.8159C18.9211 20.7107 17.781 21.116 16.3363 21.3103C14.9248 21.5 13.1158 21.5 10.8074 21.5H10.6926C8.38423 21.5 6.57519 21.5 5.16371 21.3103C3.71897 21.116 2.57895 20.7107 1.68414 19.8159C0.789327 18.9211 0.383986 17.781 0.189747 16.3363C-2.28584e-05 14.9248 -1.26631e-05 13.1158 2.1154e-07 10.8074V10.6926C-1.26631e-05 8.38423 -2.28584e-05 6.57519 0.189747 5.16371C0.383986 3.71897 0.789327 2.57895 1.68414 1.68414C2.57895 0.789327 3.71897 0.383986 5.16371 0.189747C6.57519 -2.28584e-05 8.38423 -1.26631e-05 10.6926 2.1154e-07ZM5.36358 1.67637C4.08517 1.84825 3.31445 2.17514 2.7448 2.7448C2.17514 3.31445 1.84825 4.08517 1.67637 5.36358C1.64922 5.56554 1.62624 5.77735 1.60681 6H5.33829L8.19439 1.51185C7.06691 1.52915 6.14205 1.57171 5.36358 1.67637ZM9.95717 1.50032C9.93858 1.55268 9.91385 1.60379 9.88275 1.65266L7.11626 6H11.8383L14.6689 1.55191C13.6057 1.5007 12.3247 1.5 10.75 1.5C10.477 1.5 10.2129 1.50002 9.95717 1.50032ZM16.3482 1.70698L13.6163 6H19.8932C19.8738 5.77735 19.8508 5.56554 19.8236 5.36358C19.6518 4.08517 19.3249 3.31445 18.7552 2.7448C18.2177 2.20731 17.5012 1.88594 16.3482 1.70698ZM19.9733 7.5H12.2612C12.2533 7.50013 12.2453 7.50013 12.2374 7.5H5.76122C5.75327 7.50013 5.74532 7.50013 5.73737 7.5H1.52672C1.50045 8.42173 1.5 9.49215 1.5 10.75C1.5 13.1282 1.50159 14.8364 1.67637 16.1364C1.84825 17.4148 2.17514 18.1855 2.7448 18.7552C3.31445 19.3249 4.08517 19.6518 5.36358 19.8236C6.66356 19.9984 8.37178 20 10.75 20C13.1282 20 14.8364 19.9984 16.1364 19.8236C17.4148 19.6518 18.1855 19.3249 18.7552 18.7552C19.3249 18.1855 19.6518 17.4148 19.8236 16.1364C19.9984 14.8364 20 13.1282 20 10.75C20 9.49215 19.9996 8.42173 19.9733 7.5ZM12.1182 10.6712L12.2199 10.7368C12.8397 11.1366 13.3763 11.4828 13.7504 11.8136C14.1483 12.1656 14.5 12.6186 14.5 13.25C14.5 13.8814 14.1483 14.3344 13.7504 14.6864C13.3763 15.0171 12.8397 15.3633 12.2199 15.7632L12.1182 15.8288C11.4921 16.2328 10.9479 16.584 10.4959 16.7847C10.0256 16.9935 9.42296 17.144 8.84822 16.7789C8.30356 16.4328 8.13842 15.8422 8.06912 15.3222C7.99994 14.803 7.99996 14.1174 8 13.3049V13.1951C7.99996 12.3826 7.99994 11.697 8.06912 11.1778C8.13842 10.6578 8.30356 10.0672 8.84822 9.72114C9.42296 9.356 10.0256 9.50645 10.4959 9.71528C10.9479 9.91598 11.4921 10.2672 12.1182 10.6712ZM9.60792 11.0017C9.60731 11.0009 9.61198 10.9996 9.62375 11.0004C9.61441 11.0029 9.60852 11.0025 9.60792 11.0017ZM9.64711 11.0032C9.68726 11.0101 9.76188 11.0306 9.88719 11.0862C10.2188 11.2334 10.6634 11.5177 11.3573 11.9654C12.0407 12.4064 12.4785 12.6913 12.7566 12.9373C13.0127 13.1637 13.001 13.2432 13 13.2496C13.001 13.2561 13.0127 13.3363 12.7566 13.5627C12.4785 13.8087 12.0407 14.0936 11.3573 14.5346C10.6634 14.9823 10.2188 15.2666 9.88719 15.4138C9.76188 15.4694 9.68726 15.4899 9.6471 15.4968C9.62489 15.454 9.58616 15.3506 9.55598 15.1241C9.50162 14.7162 9.5 14.1321 9.5 13.25C9.5 12.3679 9.50162 11.7838 9.55598 11.3759C9.58616 11.1494 9.6249 11.046 9.64711 11.0032ZM9.60792 15.4983C9.60853 15.4975 9.61442 15.4972 9.62375 15.4996C9.61197 15.5004 9.6073 15.4991 9.60792 15.4983ZM9.66007 15.5182C9.66351 15.5211 9.665 15.5231 9.66489 15.5233C9.66478 15.5236 9.66306 15.5222 9.66007 15.5182ZM9.66008 10.9818C9.66306 10.9778 9.66478 10.9764 9.66489 10.9767C9.665 10.9769 9.66351 10.9789 9.66008 10.9818Z"
          />
        </svg>
      );
    case 'wallet':
      // Source: icons → WalletFig (stroke)
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 26 26"
          fill="none"
          stroke={color}
          strokeWidth={1.625}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6.5 10.832H10.8333" />
          <path
            d="M22.5694 11.918H19.75C17.817 11.918 16.25 13.373 16.25 15.168C16.25 16.9629 17.817 18.418 19.75 18.418H22.5694C22.6597 18.418 22.7049 18.418 22.743 18.4156C23.3272 18.3801 23.7925 17.948 23.8308 17.4055C23.8333 17.3701 23.8333 17.3282 23.8333 17.2444V13.0916C23.8333 13.0078 23.8333 12.9658 23.8308 12.9304C23.7925 12.388 23.3272 11.9558 22.743 11.9203C22.7049 11.918 22.6597 11.918 22.5694 11.918Z"
            strokeLinecap={undefined}
            strokeLinejoin={undefined}
          />
          <path
            d="M22.7115 11.9167C22.6272 9.88834 22.3557 8.64473 21.4801 7.7692C20.2109 6.5 18.1682 6.5 14.0827 6.5H10.8327C6.74718 6.5 4.70442 6.5 3.43522 7.7692C2.16602 9.03841 2.16602 11.0812 2.16602 15.1667C2.16602 19.2522 2.16602 21.2949 3.43522 22.5641C4.70442 23.8333 6.74718 23.8333 10.8327 23.8333H14.0827C18.1682 23.8333 20.2109 23.8333 21.4801 22.5641C22.3557 21.6886 22.6272 20.445 22.7115 18.4167"
            strokeLinecap={undefined}
            strokeLinejoin={undefined}
          />
          <path
            d="M6.5 6.5L10.5468 3.81673C11.6864 3.06109 13.2303 3.06109 14.3699 3.81673L18.4167 6.5"
            strokeLinejoin={undefined}
          />
          <path d="M19.4903 15.168H19.5" strokeWidth={2.16667} />
        </svg>
      );
    case 'calendar':
      return stroke(
        <>
          <rect x="3" y="5" width="18" height="16" rx="2.5" />
          <path d="M3 9.5h18M8 3v4M16 3v4M12 13.5v4M10 15.5h4" />
        </>,
      );
    case 'edit':
      return stroke(
        <>
          <path d="M4 20h4L18.5 9.5l-4-4L4 16v4z" />
          <path d="M13.5 6.5l4 4" />
        </>,
      );
    case 'copy':
      return stroke(
        <>
          <rect x="9" y="9" width="11" height="11" rx="2.5" />
          <path d="M5 15.5A2.5 2.5 0 0 1 4 13.5V6A2.5 2.5 0 0 1 6.5 3.5H14a2.5 2.5 0 0 1 2 1" />
        </>,
      );
    case 'affiliates':
      // Source: figmaIcons → Handshake (mixed: fill + stroke)
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={color}
          stroke={color}
          strokeWidth={0.6}
        >
          <path d="M2.4075 11.37H2.4375C2.73686 11.3679 3.03088 11.2905 3.2925 11.145L3.5325 11.3438C4.11237 11.8326 4.57176 12.4486 4.875 13.1437C4.77184 13.449 4.77184 13.7797 4.875 14.085C4.94202 14.3149 5.06313 14.5255 5.22819 14.699C5.39324 14.8726 5.59746 15.0041 5.82375 15.0825C5.73845 15.2562 5.68886 15.4453 5.67791 15.6385C5.66696 15.8318 5.69487 16.0252 5.76 16.2075C5.83514 16.4301 5.96149 16.6319 6.12889 16.7967C6.29628 16.9614 6.50004 17.0846 6.72375 17.1563C6.81437 17.185 6.90717 17.2063 7.00125 17.22C6.98982 17.2515 6.98227 17.2842 6.97875 17.3175C6.9028 17.708 6.98509 18.1127 7.2075 18.4425C7.35434 18.6632 7.5559 18.8421 7.7925 18.9618C8.02911 19.0814 8.29268 19.1376 8.5575 19.125C8.68503 19.1149 8.81091 19.0898 8.9325 19.05C8.94688 19.3187 9.03512 19.5782 9.1875 19.8C9.29765 19.9654 9.43958 20.1073 9.60502 20.2173C9.77047 20.3274 9.95614 20.4035 10.1512 20.4413C10.2499 20.4622 10.3505 20.4722 10.4512 20.4713C10.7335 20.478 11.0115 20.4011 11.25 20.25L15.375 17.97C15.9712 17.6432 16.4549 17.1439 16.7625 16.5375C17.6429 14.9298 18.8274 13.5084 20.25 12.3525L20.5312 12.1312C20.7211 12.223 20.9253 12.2813 21.135 12.3038H21.3075C21.7252 12.3037 22.1297 12.1578 22.4512 11.8912C22.6335 11.7406 22.7843 11.5555 22.895 11.3466C23.0057 11.1376 23.0742 10.9089 23.0965 10.6735C23.1188 10.4381 23.0945 10.2006 23.0249 9.97454C22.9554 9.74853 22.842 9.53843 22.6912 9.35625L18.4575 4.22625C18.229 3.95225 17.9245 3.752 17.5824 3.65073C17.2403 3.54946 16.8759 3.55169 16.5351 3.65715C16.1943 3.76261 15.8923 3.96657 15.6671 4.24335C15.442 4.52012 15.3038 4.85733 15.27 5.2125C15.2547 5.40368 15.2699 5.59609 15.315 5.7825L15.1575 5.91C14.3856 6.53052 13.4448 6.90424 12.4575 6.9825C11.935 7.02605 11.417 7.11259 10.9087 7.24125C10.7925 7.24125 10.6875 7.2075 10.62 7.20375L8.5275 5.82375C8.57214 5.67653 8.59612 5.52382 8.59875 5.37C8.603 5.13342 8.56046 4.89833 8.47358 4.67824C8.3867 4.45815 8.2572 4.25739 8.0925 4.0875C7.76055 3.74534 7.30643 3.54884 6.82978 3.54111C6.35313 3.53338 5.89287 3.71504 5.55 4.04625L1.17375 8.26875C0.919969 8.51873 0.745533 8.83799 0.672264 9.18659C0.598996 9.5352 0.630148 9.89767 0.761824 10.2287C0.8935 10.5596 1.11985 10.8445 1.41257 11.0475C1.70528 11.2505 2.05135 11.3627 2.4075 11.37ZM16.4025 4.56375C16.5901 4.40819 16.8263 4.32327 17.07 4.32375H17.1712C17.3082 4.33658 17.4414 4.37631 17.563 4.44066C17.6846 4.50501 17.7923 4.59271 17.88 4.69875L22.125 9.8325C22.3024 10.0469 22.3874 10.3229 22.3614 10.6C22.3353 10.877 22.2004 11.1324 21.9863 11.31C21.777 11.4846 21.508 11.5707 21.2363 11.55C21.0991 11.5378 20.9658 11.4983 20.8441 11.4339C20.7224 11.3695 20.6148 11.2815 20.5275 11.175L16.2525 6.0525C16.1623 5.94484 16.0945 5.82024 16.0532 5.68602C16.0118 5.5518 15.9977 5.41067 16.0117 5.27092C16.0257 5.13117 16.0675 4.99563 16.1346 4.87225C16.2017 4.74888 16.2928 4.64016 16.4025 4.5525V4.56375ZM12.5212 7.72875C13.6568 7.63939 14.739 7.20939 15.6262 6.495L15.6562 6.54L19.9238 11.6587L19.7887 11.76C18.295 12.9789 17.0509 14.4751 16.125 16.1663C15.8864 16.6389 15.5132 17.0304 15.0525 17.2913L10.875 19.59C10.792 19.6469 10.6985 19.6864 10.5999 19.7064C10.5014 19.7264 10.3998 19.7263 10.3012 19.7063C10.1735 19.6843 10.0536 19.6297 9.95323 19.5476C9.85288 19.4656 9.77551 19.3589 9.72867 19.238C9.68183 19.1172 9.66712 18.9862 9.68598 18.858C9.70483 18.7297 9.75661 18.6085 9.83625 18.5063L12.6937 16.4513C12.774 16.3933 12.8281 16.306 12.8443 16.2084C12.8604 16.1108 12.8373 16.0107 12.78 15.93C12.7513 15.8897 12.7149 15.8555 12.6729 15.8294C12.631 15.8033 12.5842 15.7857 12.5354 15.7776C12.4866 15.7696 12.4367 15.7713 12.3886 15.7827C12.3405 15.794 12.2951 15.8148 12.255 15.8438L9.34125 17.94L8.88 18.2438C8.76868 18.3191 8.63917 18.3631 8.505 18.3713C8.36939 18.3842 8.23281 18.36 8.10992 18.3012C7.98704 18.2424 7.88249 18.1512 7.8075 18.0375C7.69638 17.8677 7.65614 17.6612 7.69539 17.4622C7.73464 17.2631 7.85026 17.0874 8.0175 16.9725L12.0975 14.1262C12.1791 14.0691 12.2345 13.9818 12.2518 13.8837C12.269 13.7856 12.2466 13.6847 12.1894 13.6031C12.1322 13.5216 12.0449 13.4661 11.9468 13.4488C11.8487 13.4316 11.7478 13.4541 11.6662 13.5112L7.605 16.3575C7.50862 16.4222 7.39884 16.4643 7.28388 16.4805C7.16892 16.4968 7.05178 16.4868 6.94125 16.4513C6.82751 16.413 6.72407 16.3492 6.6389 16.2647C6.55373 16.1801 6.4891 16.0772 6.45 15.9638C6.39931 15.7979 6.40451 15.62 6.46481 15.4574C6.52512 15.2947 6.63717 15.1565 6.78375 15.0637L9.52125 13.1138C9.56151 13.085 9.5957 13.0487 9.62185 13.0067C9.648 12.9647 9.66558 12.918 9.6736 12.8692C9.68162 12.8204 9.6799 12.7705 9.66856 12.7224C9.65722 12.6742 9.63646 12.6288 9.6075 12.5888C9.54958 12.5085 9.46228 12.4544 9.36464 12.4382C9.267 12.4221 9.16693 12.4452 9.08625 12.5025L6.42375 14.4C6.24213 14.4325 6.05489 14.397 5.89784 14.3001C5.7408 14.2032 5.62496 14.0519 5.5725 13.875C5.52927 13.7096 5.53976 13.5347 5.60245 13.3756C5.66514 13.2165 5.77678 13.0815 5.92125 12.99L8.58375 11.205C9.48139 11.8859 10.5945 12.2201 11.7187 12.1463C11.8162 12.1463 11.9062 12.1463 11.9925 12.1463C12.2075 12.1543 12.4211 12.1845 12.63 12.2363C13.1326 12.3469 13.6454 12.4047 14.16 12.4087C14.8292 12.4509 15.4942 12.2774 16.0575 11.9137C16.2189 11.7922 16.3503 11.6355 16.442 11.4555C16.5336 11.2755 16.5829 11.077 16.5862 10.875C16.6237 9.87375 15.7312 9.46125 14.7825 9.0225C14.2836 8.81182 13.8063 8.5531 13.3575 8.25C13.0748 8.05091 12.7763 7.87532 12.465 7.725L12.5212 7.72875ZM8.175 6.4875L10.29 7.875C10.351 7.91601 10.4227 7.93818 10.4962 7.93875C10.6222 7.94198 10.7476 7.95578 10.8712 7.98C11.6145 8.11687 12.3179 8.41794 12.93 8.86125C13.418 9.19147 13.9367 9.47402 14.4787 9.705C15.3675 10.1137 15.8662 10.3687 15.8475 10.83C15.8458 10.9191 15.8231 11.0066 15.7815 11.0854C15.7398 11.1642 15.6802 11.2322 15.6075 11.2838C15.1312 11.6588 14.0287 11.7262 12.8025 11.4675C12.5388 11.4108 12.2697 11.3831 12 11.385C11.9062 11.385 11.805 11.385 11.6925 11.385C10.9425 11.4038 9.8175 11.4338 8.1675 9.885C8.09371 9.82516 7.99995 9.79556 7.90518 9.80219C7.8104 9.80881 7.72167 9.85115 7.65692 9.92067C7.59216 9.99019 7.5562 10.0817 7.55631 10.1767C7.55642 10.2717 7.59258 10.3631 7.6575 10.4325C7.755 10.5263 7.8525 10.6125 7.95 10.695L5.505 12.375C5.46321 12.4039 5.42315 12.4352 5.385 12.4688C5.03936 11.8238 4.57348 11.251 4.0125 10.7813L3.8925 10.68L8.05125 6.63375C8.09557 6.58769 8.13691 6.53884 8.175 6.4875ZM1.695 8.805L6.06 4.57875C6.25597 4.38869 6.51826 4.28244 6.79125 4.2825C6.93133 4.28211 7.07002 4.31016 7.19894 4.36495C7.32786 4.41974 7.44431 4.50013 7.54125 4.60125C7.63955 4.69917 7.7166 4.81631 7.76758 4.94536C7.81855 5.07441 7.84235 5.21258 7.8375 5.35125C7.83832 5.49139 7.81047 5.63022 7.75566 5.7592C7.70084 5.88818 7.62022 6.00458 7.51875 6.10125L3.165 10.3238C2.96158 10.514 2.69351 10.6199 2.415 10.62C2.20587 10.6172 2.00226 10.5525 1.82992 10.434C1.65758 10.3155 1.52424 10.1486 1.44677 9.95429C1.36931 9.76002 1.35118 9.54714 1.3947 9.34257C1.43822 9.13799 1.54142 8.95092 1.69125 8.805H1.695Z" />
        </svg>
      );
    case 'influencers':
      // Source: figmaIcons → Microphone (fill)
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 17.5 20.5" fill={color}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3 5.75C3 2.57436 5.57436 0 8.75 0C11.9256 0 14.5 2.57436 14.5 5.75V8.75C14.5 11.9256 11.9256 14.5 8.75 14.5C5.57436 14.5 3 11.9256 3 8.75V5.75ZM4.56597 5H5.75C6.16421 5 6.5 5.33579 6.5 5.75C6.5 6.16421 6.16421 6.5 5.75 6.5H4.5V8H5.75C6.16421 8 6.5 8.33579 6.5 8.75C6.5 9.16421 6.16421 9.5 5.75 9.5H4.56597C4.9202 11.4895 6.6587 13 8.75 13C10.8413 13 12.5798 11.4895 12.934 9.5L10.25 9.5C9.83579 9.5 9.5 9.16421 9.5 8.75C9.5 8.33579 9.83579 8 10.25 8L13 8V6.5H10.25C9.83579 6.5 9.5 6.16421 9.5 5.75C9.5 5.33579 9.83579 5 10.25 5H12.934C12.5798 3.01049 10.8413 1.5 8.75 1.5C6.6587 1.5 4.9202 3.01049 4.56597 5ZM0.75 7C1.16421 7 1.5 7.33579 1.5 7.75V8.75C1.5 12.7541 4.74594 16 8.75 16C12.7541 16 16 12.7541 16 8.75V7.75C16 7.33579 16.3358 7 16.75 7C17.1642 7 17.5 7.33579 17.5 7.75V8.75C17.5 13.3298 13.9814 17.0879 9.5 17.4683V19.75C9.5 20.1642 9.16421 20.5 8.75 20.5C8.33579 20.5 8 20.1642 8 19.75V17.4683C3.5186 17.0879 0 13.3298 0 8.75V7.75C0 7.33579 0.335786 7 0.75 7Z"
          />
        </svg>
      );
    case 'search':
      return stroke(
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="M16.5 16.5L21 21" />
        </>,
      );
    case 'clock':
      return stroke(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </>,
      );
    case 'vibe':
      // Vibe: two symmetric "vibe" waves radiating from the centre (no heart), sized to fill the icon box.
      return stroke(
        <>
          <path d="M10 8a4 5 0 0 0 0 8" />
          <path d="M6.8 5.5a6.5 7.5 0 0 0 0 13" />
          <path d="M14 8a4 5 0 0 1 0 8" />
          <path d="M17.2 5.5a6.5 7.5 0 0 1 0 13" />
        </>,
      );
    case 'document':
      // Prompt: a page with a folded corner and two text lines.
      return stroke(
        <>
          <path d="M14 3H7a1.8 1.8 0 0 0-1.8 1.8v14.4A1.8 1.8 0 0 0 7 21h10a1.8 1.8 0 0 0 1.8-1.8V8z" />
          <path d="M14 3v5h4.8" />
          <path d="M8.5 13h7" />
          <path d="M8.5 16.5h7" />
        </>,
      );
    case 'palette':
      // Mood board: a painter's palette blob with a thumb notch and three paint dots.
      return stroke(
        <>
          <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.1 0 1.7-1 1.4-2-.3-1 .4-2 1.5-2H17a3.5 3.5 0 0 0 3.5-3.5C20.5 7.3 16.7 3.5 12 3.5Z" />
          <circle cx="8" cy="10.5" r="1" fill={color} stroke="none" />
          <circle cx="12" cy="7.5" r="1" fill={color} stroke="none" />
          <circle cx="15.5" cy="9.5" r="1" fill={color} stroke="none" />
        </>,
      );
    case 'lock':
      // Padlock: body + shackle + keyhole. Marks a gated card under the LockVeil.
      return stroke(
        <>
          <rect x="5" y="10" width="14" height="10.5" rx="2.2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          <path d="M12 14v3" />
        </>,
      );
    case 'trending-up':
      // Source: icons → TrendUp (stroke)
      return stroke(
        <>
          <path d="M4 16l5-5 3 3 7-7" />
          <path d="M15 7h5v5" />
        </>,
      );
    case 'trending-down':
      // Source: icons → TrendUp (stroke), vertically flipped around centre (viewBox 0 0 24 24)
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <g transform="translate(0,24) scale(1,-1)">
            <path d="M4 16l5-5 3 3 7-7" />
            <path d="M15 7h5v5" />
          </g>
        </svg>
      );
    case 'arrow-left':
      // Source: figmaIcons → ArrowUp (fill variant), rotated -90° to point left
      // so it reads as a back arrow. Square viewBox keeps it undistorted.
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 14.5833 14.5833" fill={color}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            transform="translate(0 12.91665) rotate(-90)"
            d="M5.18306 0.183058C5.42714 -0.0610194 5.82286 -0.0610194 6.06694 0.183058L11.0669 5.18306C11.311 5.42714 11.311 5.82286 11.0669 6.06694C10.8229 6.31102 10.4271 6.31102 10.1831 6.06694L6.25 2.13388L6.25 13.9583C6.25 14.3035 5.97018 14.5833 5.625 14.5833C5.27982 14.5833 5 14.3035 5 13.9583L5 2.13388L1.06694 6.06694C0.822864 6.31102 0.427136 6.31102 0.183058 6.06694C-0.0610194 5.82286 -0.0610194 5.42714 0.183058 5.18306L5.18306 0.183058Z"
          />
        </svg>
      );
    case 'heart':
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path
            d="M12 20s-7-4.6-9.3-9C1.2 8 2.6 5 5.6 5c1.9 0 3.2 1.1 3.9 2.2C10.2 6.1 11.5 5 13.4 5c3 0 4.4 3 2.9 6-2.3 4.4-9.3 9-9.3 9z"
            transform="translate(1.7 -0.5)"
          />
        </svg>
      );
    case 'check':
      return stroke(
        <>
          <path d="M5 12.5l4.5 4.5L19 7" />
        </>,
      );
    case 'plus':
      return stroke(
        <>
          <path d="M12 5v14M5 12h14" />
        </>,
      );
    case 'plus-circle':
      return stroke(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8.5v7M8.5 12h7" />
        </>,
      );
    case 'download':
      return stroke(
        <>
          <path d="M12 4v10" />
          <path d="M8 10.5l4 4 4-4" />
          <path d="M5 19h14" />
        </>,
      );
    case 'chevron-expand':
      return stroke(
        <>
          <path d="M8.5 9.5L12 6l3.5 3.5" />
          <path d="M8.5 14.5L12 18l3.5-3.5" />
        </>,
      );
    case 'chevron-up':
      // Single up chevron — the mobile reorder buttons rotate it 180° for "move down".
      return stroke(
        <>
          <path d="M6 15l6-6 6 6" />
        </>,
      );
    case 'grab':
      // Source: user's grab (1).svg — a WHITE hand with a BLACK outline + a soft bottom shadow. Rendered
      // verbatim via theme-FIXED tokens (white `onAccent` fill + black `cursorInk`), so it stays white+black
      // in BOTH themes (a cursor doesn't invert with the app theme). It ignores the generic `color` prop.
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 512 512">
          <path
            fill={COLORS.onAccent}
            d="M128 199.111c0-23.467-19.2-42.667-42.667-42.667s-42.667 19.2-42.667 42.667v14.222c0 85.333 34.361 148.139 71.111 199.68 33.792 47.417 96.142 84.764 162.702 84.764 106.667 0 192.853-86.187 192.853-192.853V113.778c0-23.609-19.058-42.667-42.667-42.667S384 90.169 384 113.778v42.667-71.112c0-23.609-19.058-42.667-42.667-42.667s-42.667 19.058-42.667 42.667v71.111-99.555c0-23.609-19.058-42.667-42.667-42.667s-42.667 19.058-42.667 42.667v99.556-71.112c0-23.609-19.058-42.667-42.667-42.667S128 61.724 128 85.333v156.444z"
          />
          <path
            fill={COLORS.cursorInk}
            d="M56.889 199.111c0-15.701 12.772-28.444 28.444-28.444s28.444 12.743 28.444 28.444v42.667c0 7.851 6.372 14.222 14.222 14.222s14.222-6.372 14.222-14.222V85.333c0-15.701 12.772-28.444 28.444-28.444s28.444 12.743 28.444 28.444v71.111c0 7.851 6.372 14.222 14.222 14.222 7.851 0 14.222-6.372 14.222-14.222V56.889c0-15.701 12.772-28.444 28.444-28.444s28.444 12.743 28.444 28.444v99.556c0 7.851 6.372 14.222 14.222 14.222 7.851 0 14.222-6.372 14.222-14.222V85.333c0-15.701 12.772-28.444 28.444-28.444s28.444 12.743 28.444 28.444v71.111c0 7.851 6.372 14.222 14.222 14.222s14.222-6.372 14.222-14.222v-42.667c0-15.701 12.772-28.444 28.444-28.444 15.673 0 28.444 12.743 28.444 28.444v191.147c0 98.503-80.128 178.631-178.631 178.631-57.685 0-117.02-30.948-151.125-78.82C91.5 357.261 56.883 297.357 56.883 213.332v-14.221zM276.48 512c114.176 0 207.076-92.9 207.076-207.076V113.778c0-31.374-25.515-56.889-56.889-56.889a56.43 56.43 0 0 0-31.63 9.643c-7.794-22.158-28.928-38.087-53.703-38.087a56.43 56.43 0 0 0-31.63 9.643C301.909 15.929 280.775 0 256 0s-45.909 15.929-53.703 38.087a56.43 56.43 0 0 0-31.63-9.643c-31.374 0-56.889 25.515-56.889 56.889v64.54a56.43 56.43 0 0 0-28.444-7.652c-31.374 0-56.889 25.515-56.889 56.889v14.222c0 92.615 39.424 159.744 73.756 207.929C141.483 476.388 209.892 512 276.48 512"
          />
          <path
            fill={COLORS.cursorInk}
            opacity={0.12}
            d="M276.48 512c114.204 0 207.076-92.9 207.076-207.076v-56.889a14.19 14.19 0 0 0-14.222-14.222c-7.851 0-14.222 6.343-14.222 14.222 0 98.503-80.128 178.631-178.631 178.631-57.685 0-116.992-30.948-151.097-78.82-27.335-38.315-59.648-91.278-66.93-161.252-.683-6.827-6.172-12.146-12.971-12.715-6.656-.569-13.084 3.812-14.905 10.411-1.422 5.291-2.133 10.126-2.133 14.82v14.222c0 92.644 39.424 159.772 73.756 207.929C141.483 476.388 209.863 512 276.48 512"
          />
        </svg>
      );
    case 'filter':
      return stroke(
        <>
          <path d="M4 6h16M7 12h10M10 18h4" />
        </>,
      );
    case 'pause':
      // Two 4px-thick bars with rounded caps and a 4px gap between them
      // (bars at x=8/16 → edges at 10/14 → 4px gap). Needs its own svg
      // because the shared `stroke` helper hard-codes strokeWidth 1.6.
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 8v8M16 8v8" />
        </svg>
      );
    case 'stop':
      // Filled square with a 1px corner radius.
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <rect x="6.5" y="6.5" width="11" height="11" rx="1" />
        </svg>
      );
    case 'close':
      return stroke(
        <>
          <path d="M6 6l12 12M18 6L6 18" />
        </>,
      );
    case 'close-circle':
      // A dismissed/blocked state, drawn on the same r=9 ring as `clock` and
      // `info` so the three read as one family when they sit in the same column.
      return stroke(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.3 9.3l5.4 5.4M14.7 9.3l-5.4 5.4" />
        </>,
      );
    case 'minus':
      return stroke(<path d="M5 12h14" />);
    case 'info':
      return stroke(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5M12 8h.01" />
        </>,
      );
    case 'warning':
      return stroke(
        <>
          <path d="M12 4l9 16H3l9-16z" />
          <path d="M12 10v4M12 17h.01" />
        </>,
      );
    case 'settings':
      // Cog/gear: a centred hub ringed by the classic notched toothing.
      return stroke(
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </>,
      );
    case 'external-link':
      // Box with an arrow springing out of its top-right corner (open in a new tab).
      return stroke(
        <>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <path d="M15 3h6v6" />
          <path d="M10 14L21 3" />
        </>,
      );
    case 'more':
      // Vertical kebab: the "more actions" affordance next to a status or a row.
      // Filled dots rather than stroked rings so they stay legible at 16-20px.
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <circle cx="12" cy="5" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="12" cy="19" r="1.75" />
        </svg>
      );
    case 'user':
      // Marks something the owner made themselves (their own game lists), rather than the site's.
      return stroke(
        <>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
        </>,
      );
    case 'user-filled':
      // The solid twin of `user`, for marks that should read at small sizes (a chip's leading glyph).
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <circle cx="12" cy="8" r="3.6" />
          <path d="M12 13.4c-3.7 0-6.7 2.3-6.7 5.1 0 .8.6 1.5 1.4 1.5h10.6c.8 0 1.4-.7 1.4-1.5 0-2.8-3-5.1-6.7-5.1z" />
        </svg>
      );
    case 'eye':
      // Preview / "see it as a reader would": the classic lid outline plus pupil.
      return stroke(
        <>
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
          <circle cx="12" cy="12" r="3" />
        </>,
      );
    case 'trash':
      // Destructive action (delete a draft): lid, bin, and the two content rules.
      return stroke(
        <>
          <path d="M4 7h16" />
          <path d="M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7" />
          <path d="M6.5 7l.8 12.1A1.5 1.5 0 0 0 8.8 20.5h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
          <path d="M10.5 11v5.5M13.5 11v5.5" />
        </>,
      );
    case 'drag-handle':
      // Grip for a sortable row: two columns of dots, the universal "drag me".
      return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      );
    case 'megaphone':
      // Bullhorn / marketing glyph (Material Symbols `campaign`, outlined): the same mark as the
      // Marketing nav glyph and the campaign-card placeholder, so the affiliates Campaigns tab reads
      // consistently. Filled path (follows `color`), not a stroke.
      return (
        <svg className={className} width={size} height={size} viewBox="0 -960 960 960" fill="none">
          <path
            fill={color}
            d="M718.46-450v-60h144.62v60H718.46Zm45.69 269.23-115.69-86.77 36.46-47.84 115.69 86.76-36.46 47.85Zm-80.77-465.38L646.92-694l115.69-86.77 36.47 47.85-115.7 86.77ZM210-220.77v-156.15h-40.77q-29.92 0-51.11-21.2-21.2-21.19-21.2-51.11v-61.54q0-29.92 21.2-51.11 21.19-21.2 51.11-21.2h154.62L503.08-690v420L323.85-376.92H270v156.15h-60Zm233.08-155.84v-206.78l-102.62 60.31H169.23q-4.61 0-8.46 3.85-3.85 3.85-3.85 8.46v61.54q0 4.61 3.85 8.46 3.85 3.85 8.46 3.85h171.23l102.62 60.31Zm115.38 21.38v-249.54q23.54 21.31 37.92 53.69 14.39 32.39 14.39 71.08t-14.39 71.08q-14.38 32.38-37.92 53.69ZM300-480Z"
          />
        </svg>
      );
    case 'grid':
      // The pool / library: a 2×2 grid of rounded tiles.
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case 'mail':
      return stroke(
        <>
          <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
          <path d="M3.5 7l8.5 6 8.5-6" />
        </>,
      );
    case 'send':
      return stroke(
        <>
          <path d="M21 3L10.5 13.5" />
          <path d="M21 3l-6.5 18-4-7.5L3 9.5 21 3z" />
        </>,
      );
    case 'refresh':
      return stroke(
        <>
          <path d="M20 12a8 8 0 1 1-2.35-5.65" />
          <path d="M20 4v4.5h-4.5" />
        </>,
      );
    case 'eye-off':
      // The `eye` outline plus a diagonal slash: preview disabled / hidden field.
      return stroke(
        <>
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
          <circle cx="12" cy="12" r="3" />
          <path d="M4 4l16 16" />
        </>,
      );
    case 'shield':
      return stroke(<path d="M12 3l7 3v5.5c0 4.5-3 8-7 9.5-4-1.5-7-5-7-9.5V6l7-3z" />);
    case 'chevron-down':
      return stroke(<path d="M6 9l6 6 6-6" />);
    case 'image':
      // Picture glyph: a rounded-rect frame, a small "sun" circle and a mountain path.
      return stroke(
        <>
          <rect x="3" y="4" width="18" height="16" rx="2.5" />
          <circle cx="8.5" cy="9" r="1.5" />
          <path d="M21 15l-5-5-7 7" />
        </>,
      );
    case 'preview':
      // Preview on site: a browser window (its frame and top bar) with an eye in it, after Material Symbols' `preview`,
      // so it reads apart from the bare `eye` of Show list / Hide list on the same row.
      return stroke(
        <>
          <rect x="3" y="3.5" width="18" height="17" rx="2.5" />
          <path d="M3 8h18" />
          <path d="M6.5 14.25s2-3.5 5.5-3.5 5.5 3.5 5.5 3.5-2 3.5-5.5 3.5-5.5-3.5-5.5-3.5z" />
          <circle cx="12" cy="14.25" r="1.25" />
        </>,
      );
    case 'reorder':
      // Reorder (the Games tab's Reorder lists): an up arrow beside a down arrow, after Material Symbols' `swap_vert`.
      return stroke(
        <>
          <path d="M8.5 19.5V4.5" />
          <path d="M4.5 8.5l4-4 4 4" />
          <path d="M15.5 4.5v15" />
          <path d="M11.5 15.5l4 4 4-4" />
        </>,
      );
    default:
      return null;
  }
};

export default Icon;
